import React, { createContext, forwardRef, useContext, useMemo } from 'react';

import { radioGroupItems } from './RadioGroup.css';
import { Field } from '../Field';

export interface RadioGroupContextValue {
  name?: string;
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
}

const RadioGroupContext = createContext<RadioGroupContextValue | undefined>(undefined);

export const useRadioGroup = () => {
  return useContext(RadioGroupContext);
};

export interface RadioGroupProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  label?: React.ReactNode;
  name?: string;
  value?: string;
  onChange?: (value: string) => void;
  direction?: 'vertical' | 'horizontal';
  gap?: number;
  disabled?: boolean;
  error?: string;
  required?: boolean;
  children?: React.ReactNode;
}

export const RadioGroup = forwardRef<HTMLDivElement, RadioGroupProps>(
  (
    {
      label,
      name,
      value,
      onChange,
      direction = 'vertical',
      gap = 12,
      disabled = false,
      error,
      required = false,
      children,
      className,
      ...props
    },
    ref,
  ) => {
    const contextValue = useMemo(
      () => ({
        name,
        value,
        onChange,
        disabled,
      }),
      [name, value, onChange, disabled],
    );

    return (
      <RadioGroupContext.Provider value={contextValue}>
        <Field
          ref={ref}
          role="radiogroup"
          label={typeof label === 'string' ? label : String(label ?? '')}
          required={required}
          error={error}
          className={className}
          {...props}
        >
          <div className={radioGroupItems({ direction })} style={{ gap: `${gap}px` }}>
            {children}
          </div>
        </Field>
      </RadioGroupContext.Provider>
    );
  },
);

RadioGroup.displayName = 'RadioGroup';
